import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Paper,
  CircularProgress,
  Button,
  Tab,
  Tabs,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Terminal as TerminalIcon,
  ShowChart as ChartIcon,
} from '@mui/icons-material';
import { CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, LineChart, Line } from 'recharts';
import { getContainerMetrics } from '../api/services/monitoring/Monitoring';
import { useTheme } from '@mui/material/styles';

const TerminalModal = ({ open, onClose, containerName, nodeId }) => {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [metricsData, setMetricsData] = useState([]);
  const streamReaderRef = useRef(null);
  const readerActiveRef = useRef(true);
  const logsEndRef = useRef(null);

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [logs]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Helper function to parse network and block I/O strings to numbers
  const parseIoString = (ioString) => {
    if (!ioString || typeof ioString !== 'string') return 0;

    try {
      // Handle formats like "1.01MB / 69.7MB" or "7.31MB / 373kB"
      const parts = ioString.split('/').map(part => part.trim());
      if (parts.length >= 1) {
        const firstPart = parts[0];
        const match = firstPart.match(/([\d.]+)([A-Za-z]+)/);
        if (match) {
          const value = parseFloat(match[1]);
          const unit = match[2].toLowerCase();

          if (!isNaN(value)) {
            // Convert to MB for consistent scaling
            if (unit.includes('gb') || unit.includes('g')) {
              return value * 1024;
            } else if (unit.includes('kb') || unit.includes('k')) {
              return value / 1024;
            } else {
              return value; // Assume MB
            }
          }
        }
      }
      return 0;
    } catch (error) {
      console.debug('Error parsing I/O string:', error.message);
      return 0;
    }
  };

  const parseMetricsForCharts = (metricData) => {
    try {
      // Validate input data
      if (!metricData || typeof metricData !== 'object') return;

      // Check if we have valid metrics
      const hasValidMetrics = typeof metricData.cpuPercent === 'number' ||
                             typeof metricData.memoryPercent === 'number' ||
                             typeof metricData.memoryUsage === 'number';

      if (!hasValidMetrics) return;

      const currentTime = new Date().toLocaleTimeString('en-US', {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      const newDataPoint = {
        time: currentTime,
        cpuPercent: typeof metricData.cpuPercent === 'number' && !isNaN(metricData.cpuPercent) ? metricData.cpuPercent : 0,
        memoryPercent: typeof metricData.memoryPercent === 'number' && !isNaN(metricData.memoryPercent) ? metricData.memoryPercent : 0,
        memoryUsage: typeof metricData.memoryUsage === 'number' && !isNaN(metricData.memoryUsage) ? metricData.memoryUsage / (1024 * 1024 * 1024) : 0, // Convert to GB
        networkIo: parseIoString(metricData.networkIo), // Convert string to number
        blockIo: parseIoString(metricData.blockIo), // Convert string to number
        networkIoDisplay: typeof metricData.networkIo === 'string' ? metricData.networkIo : '0MB / 0MB', // Keep original for display
        blockIoDisplay: typeof metricData.blockIo === 'string' ? metricData.blockIo : '0MB / 0MB' // Keep original for display
      };

      setMetricsData(prev => {
        const updated = [...prev, newDataPoint];
        // Keep only last 20 data points to avoid memory issues
        return updated.slice(-20);
      });
    } catch (error) {
      console.debug('Error parsing metrics for charts:', error.message);
    }
  };

  useEffect(() => {
    if (open && containerName && nodeId) {
      startContainerStream();
    }

    return () => {
      cleanupStream();
    };
  }, [open, containerName, nodeId]);

  const cleanupStream = () => {
    readerActiveRef.current = false;
    if (streamReaderRef.current) {
      streamReaderRef.current.cancel().catch(e => console.log('Error cancelling stream:', e));
      streamReaderRef.current = null;
    }
  };

  const startContainerStream = async () => {
    if (!containerName || !nodeId) return;

    setIsLoading(true);
    setError(null);
    setLogs([]);
    setIsStreaming(true);

    try {
      cleanupStream(); // Clean up any existing stream

      const timestamp = new Date().toLocaleString();
      setLogs(prev => [...prev, `[${timestamp}] Connecting to container metrics stream for "${containerName}"...`]);

      const { streamReader, error } = await getContainerMetrics(nodeId, containerName);

      if (error || !streamReader) {
        throw new Error(error || 'Failed to establish stream connection');
      }

      setLogs(prev => [...prev, `[${new Date().toLocaleString()}] Stream connected successfully. Receiving metrics...`]);
      streamReaderRef.current = streamReader;
      readerActiveRef.current = true;

      readContainerStream(streamReader);
    } catch (err) {
      console.error('Error starting container stream:', err);
      setError(err.message);
      setLogs(prev => [...prev, `[${new Date().toLocaleString()}] ERROR: ${err.message}`]);
    } finally {
      setIsLoading(false);
    }
  };

  const readContainerStream = async (reader) => {
    try {
      while (readerActiveRef.current) {
        const { done, value } = await reader.read();
        if (done) break;

        try {
          if (value) {
            const timestamp = new Date().toLocaleString();
            let logEntry = `[${timestamp}] `;

            if (typeof value === 'string') {
              logEntry += value;
            } else if (value && typeof value === 'object' && value.result && value.result.metrics) {
              // Format the metrics data nicely
              const metrics = value.result.metrics;

              // Validate metrics data structure more thoroughly
              const hasValidMetrics = (
                (typeof metrics.cpuPercent === 'number' && !isNaN(metrics.cpuPercent)) ||
                (typeof metrics.memoryPercent === 'number' && !isNaN(metrics.memoryPercent)) ||
                (typeof metrics.memoryUsage === 'number' && !isNaN(metrics.memoryUsage))
              );

              if (hasValidMetrics) {
                logEntry += `METRICS: ${JSON.stringify(metrics, null, 2)}`;

                // Parse metrics for charts with additional validation
                try {
                  parseMetricsForCharts(metrics);
                } catch (chartError) {
                  console.debug('Error parsing metrics for charts:', chartError.message);
                }
              } else {
                // Skip invalid metrics silently to prevent spam
                console.debug('Skipping invalid metrics data');
                return; // Don't add to logs
              }
            } else if (value && typeof value === 'object') {
              // Check if it's a status message or other valid data
              if (value.result && (value.result.message || value.result.outputType)) {
                logEntry += JSON.stringify(value, null, 2);
              } else {
                // Skip unknown object structures
                return;
              }
            } else {
              logEntry += String(value);
            }

            setLogs(prev => [...prev, logEntry]);
          }
        } catch (parseError) {
          // Silently handle parsing errors for individual data points
          console.debug('Skipping malformed data point in terminal stream:', parseError.message);
          // Don't add error messages to logs to prevent spam
        }
      }
    } catch (err) {
      console.error('Error reading container stream:', err);
      if (readerActiveRef.current) {
        setError(`Stream error: ${err.message}`);
        setLogs(prev => [...prev, `[${new Date().toLocaleString()}] STREAM ERROR: ${err.message}`]);
      }
    } finally {
      setIsStreaming(false);
    }
  };

  const handleClose = () => {
    cleanupStream();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          maxHeight: '90vh',
          height: 'auto',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TerminalIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
            {containerName}
          </Typography>
          {isStreaming && (
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: theme.palette.success.main,
              animation: 'pulse 2s infinite'
            }} />
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.primary.main,
              bgcolor: `${theme.palette.primary.main}10`
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: '56px',
              px: 2,
              '& .MuiTab-root': {
                minHeight: '56px',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          >
            <Tab
              icon={<TerminalIcon />}
              label="Terminal"
              iconPosition="start"
              sx={{ minHeight: '56px' }}
            />
            <Tab
              icon={<ChartIcon />}
              label="Charts"
              iconPosition="start"
              sx={{ minHeight: '56px' }}
            />
          </Tabs>
        </Box>

        {/* Terminal Tab */}
        {activeTab === 0 && (
          <Box sx={{
            flex: 1,
            bgcolor: theme.palette.background.default,
            p: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            lineHeight: 1.6
          }}>
          {/* Error Display */}
          {error && (
            <Box sx={{
              bgcolor: `${theme.palette.error.main}10`,
              border: `1px solid ${theme.palette.error.main}30`,
              p: 1.5,
              mb: 2,
              borderRadius: 1,
            }}>
              <Typography sx={{
                color: theme.palette.error.main,
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.8125rem',
                fontWeight: 500
              }}>
                Error: {error}
              </Typography>
            </Box>
          )}

          {/* Empty State */}
          {logs.length === 0 && !isLoading && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              color: theme.palette.text.secondary
            }}>
              <TerminalIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ textAlign: 'center', color: theme.palette.text.secondary }}>
                No metrics data
              </Typography>
            </Box>
          )}

          {/* Log Output */}
          {logs.map((log, index) => (
            <Box
              key={index}
              sx={{
                mb: 0.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  mr: 1,
                  fontFamily: theme.typography.fontFamily
                }}
              >
                [{log.split(']')[0]}]
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: log.includes('ERROR')
                    ? theme.palette.error.main
                    : log.includes('METRICS')
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  fontSize: '0.8125rem'
                }}
              >
                {log.split(']').slice(1).join(']').trim()}
              </Typography>
            </Box>
          ))}

          <div ref={logsEndRef} />
          </Box>
        )}

        {/* Charts Tab */}
        {activeTab === 1 && (
          <Box sx={{
            flex: 1,
            bgcolor: theme.palette.background.default,
            p: 3,
            overflow: 'auto'
          }}>
            {metricsData.length === 0 ? (
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '60vh',
                color: theme.palette.text.secondary
              }}>
                <ChartIcon sx={{ fontSize: 64, mb: 3, opacity: 0.5 }} />
                <Typography variant="h5" sx={{ mb: 2 }}>
                  No Chart Data Available
                </Typography>
                <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 400 }}>
                  Charts will appear once metrics data starts streaming from the container
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Current Metrics Summary - Single thin line */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  overflowX: 'auto',
                  pb: 1,
                  '&::-webkit-scrollbar': {
                    height: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.background.default,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: '2px',
                  }
                }}>
                  <Paper sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    textAlign: 'center',
                    minWidth: 120,
                    flexShrink: 0
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                      CPU
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, lineHeight: 1 }}>
                      {metricsData[metricsData.length - 1]?.cpuPercent?.toFixed(1) || '0.0'}%
                    </Typography>
                  </Paper>
                  <Paper sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    textAlign: 'center',
                    minWidth: 120,
                    flexShrink: 0
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                      Memory
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.info.main, fontWeight: 600, lineHeight: 1 }}>
                      {metricsData[metricsData.length - 1]?.memoryPercent?.toFixed(1) || '0.0'}%
                    </Typography>
                  </Paper>
                  <Paper sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    textAlign: 'center',
                    minWidth: 140,
                    flexShrink: 0
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                      Network I/O
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.warning.main, fontWeight: 600, lineHeight: 1, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {metricsData[metricsData.length - 1]?.networkIoDisplay || 'No data'}
                    </Typography>
                  </Paper>
                  <Paper sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    textAlign: 'center',
                    minWidth: 140,
                    flexShrink: 0
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                      Block I/O
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600, lineHeight: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        R: {metricsData[metricsData.length - 1]?.blockIoDisplay?.split(' / ')[0] || '0MB'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600, lineHeight: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        W: {metricsData[metricsData.length - 1]?.blockIoDisplay?.split(' / ')[1] || '0MB'}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>


                {/* Charts - 2 per row */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  {/* CPU Chart */}
                  <Paper sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    height: 280
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: theme.palette.primary.main
                      }} />
                      <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                        CPU Usage
                      </Typography>
                      <Chip
                        label={`${metricsData[metricsData.length - 1]?.cpuPercent?.toFixed(1) || '0.0'}%`}
                        size="small"
                        sx={{
                          bgcolor: `${theme.palette.primary.main}20`,
                          color: theme.palette.primary.main,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Box sx={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={metricsData}>
                          <defs>
                            <linearGradient id="cpuGradientHalf" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                              <stop offset="50%" stopColor={theme.palette.primary.main} stopOpacity={0.4}/>
                              <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis
                            dataKey="time"
                            stroke={theme.palette.text.secondary}
                            fontSize={10}
                            tick={{ fill: theme.palette.text.secondary }}
                          />
                          <YAxis
                            stroke={theme.palette.text.secondary}
                            fontSize={10}
                            tick={{ fill: theme.palette.text.secondary }}
                            domain={[0, 100]}
                            label={{ value: '%', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: theme.palette.text.secondary } }}
                          />
                          <ChartTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)'
                            }}
                            labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                          />
                          <Area
                            type="monotone"
                            dataKey="cpuPercent"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#cpuGradientHalf)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  {/* Memory Chart */}
                  <Paper sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    height: 280
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: theme.palette.info.main
                      }} />
                      <Typography variant="h6" sx={{ color: theme.palette.info.main, fontWeight: 600 }}>
                        Memory Usage
                      </Typography>
                      <Chip
                        label={`${metricsData[metricsData.length - 1]?.memoryPercent?.toFixed(1) || '0.0'}%`}
                        size="small"
                        sx={{
                          bgcolor: `${theme.palette.info.main}20`,
                          color: theme.palette.info.main,
                          fontWeight: 600
                        }}
                      />
                    </Box>
                    <Box sx={{ height: 220 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={metricsData}>
                          <defs>
                            <linearGradient id="memoryGradientHalf" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={theme.palette.info.main} stopOpacity={0.8}/>
                              <stop offset="50%" stopColor={theme.palette.info.main} stopOpacity={0.4}/>
                              <stop offset="95%" stopColor={theme.palette.info.main} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                          <XAxis
                            dataKey="time"
                            stroke={theme.palette.text.secondary}
                            fontSize={10}
                            tick={{ fill: theme.palette.text.secondary }}
                          />
                          <YAxis
                            stroke={theme.palette.text.secondary}
                            fontSize={10}
                            tick={{ fill: theme.palette.text.secondary }}
                            domain={[0, 100]}
                            label={{ value: '%', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: theme.palette.text.secondary } }}
                          />
                          <ChartTooltip
                            contentStyle={{
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              borderRadius: 1,
                              boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.15)'
                            }}
                            labelStyle={{ color: theme.palette.text.primary, fontWeight: 600 }}
                          />
                          <Area
                            type="monotone"
                            dataKey="memoryPercent"
                            stroke={theme.palette.info.main}
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#memoryGradientHalf)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Box>
                  </Paper>

                  {/* Network I/O - Text Display */}
                  <Paper sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 140
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: theme.palette.warning.main
                      }} />
                      <Typography variant="h6" sx={{ color: theme.palette.warning.main, fontWeight: 600 }}>
                        Network I/O
                      </Typography>
                    </Box>
                    <Typography variant="h4" sx={{
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                      fontFamily: 'monospace',
                      textAlign: 'center'
                    }}>
                      {metricsData[metricsData.length - 1]?.networkIoDisplay || 'No data'}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: theme.palette.text.secondary,
                      textAlign: 'center',
                      mt: 1
                    }}>
                      Real-time network traffic
                    </Typography>
                  </Paper>

                  {/* Block I/O - Text Display */}
                  <Paper sx={{
                    p: 2,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.background.paper,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 140
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: theme.palette.error.main
                      }} />
                      <Typography variant="h6" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                        Block I/O
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{
                          color: theme.palette.error.main,
                          fontWeight: 600,
                          fontFamily: 'monospace'
                        }}>
                          {metricsData[metricsData.length - 1]?.blockIoDisplay?.split(' / ')[0] || '0MB'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          Read
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" sx={{
                          color: theme.palette.error.main,
                          fontWeight: 600,
                          fontFamily: 'monospace'
                        }}>
                          {metricsData[metricsData.length - 1]?.blockIoDisplay?.split(' / ')[1] || '0MB'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          Write
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{
                      color: theme.palette.text.secondary,
                      textAlign: 'center',
                      mt: 1
                    }}>
                      Disk I/O operations
                    </Typography>
                  </Paper>
                </Box>

              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <style jsx>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes progressPulse {
          0% { width: 40%; }
          50% { width: 80%; }
          100% { width: 40%; }
        }
      `}</style>
    </Dialog>
  );
};

export default TerminalModal;
